import { useState } from 'react';
import { usePostApproval } from '../lib/usePostApproval.js';

/**
 * Inline action buttons for a post row — one set per lifecycle state so the
 * Actions column is never empty for an actionable post (ADR-0027 / ADR-0029).
 *
 *   rendered / review   → Approve · Reject
 *   approved            → Publish now
 *   failed (publish)    → Retry        (re-attempt publish; idempotent)
 *   failed (blocked)    → Regenerate hint (content gate blocked it — the row
 *                         opens the post where the regenerate box lives)
 *   cancelled           → Retry        (regenerate a fresh AI draft)
 *   published           → Publish N more  (backfill the N connected accounts this
 *                         post never reached — e.g. IG/X added after it published
 *                         to FB; idempotent, never double-posts. N honors the post's
 *                         target overrides. Per-platform retry of a *failed* platform
 *                         still lives in the State cell.) Hidden when N = 0.
 *   other               → (none)
 *
 * Every button stops click propagation so it works inside a row that is itself
 * a <Link>. Errors surface inline (a publish on a content-blocked post returns
 * a 409 telling the user to regenerate — we show that message).
 */
const APPROVABLE = new Set(['rendered', 'review']);

export function PostActions({ post }) {
  const [err, setErr] = useState(null);
  const { approve, reject, publishNow, regenerateFresh } = usePostApproval(post.id, {
    onError: (e) => setErr(e?.body?.detail || e?.body?.error || e?.message || 'Action failed'),
    onApprove: () => setErr(null),
    onReject: () => setErr(null),
    onPublish: () => setErr(null),
    onRegenerate: () => setErr(null),
  });

  const busy = approve.isPending || reject.isPending || publishNow.isPending || regenerateFresh.isPending;
  const stop = (fn) => (e) => { e.preventDefault(); e.stopPropagation(); fn(); };
  const blocked = post.error_code === 'content_blocked';

  // Build the right control set for this state.
  let controls = null;

  if (APPROVABLE.has(post.state)) {
    controls = (
      <>
        <button
          type="button"
          className="post-act post-act-approve post-act-icon"
          disabled={busy}
          onClick={stop(() => { setErr(null); approve.mutate(); })}
          title="Approve this post"
          aria-label="Approve this post"
        >
          {approve.isPending ? '…' : '✓'}
        </button>
        <button
          type="button"
          className="post-act post-act-reject post-act-icon"
          disabled={busy}
          onClick={stop(() => { if (window.confirm('Reject this post? It will be cancelled.')) { setErr(null); reject.mutate(); } })}
          title="Reject this post"
          aria-label="Reject this post"
        >
          {reject.isPending ? '…' : '✕'}
        </button>
      </>
    );
  } else if (post.state === 'approved') {
    controls = (
      <button
        type="button"
        className="post-act post-act-publish"
        disabled={busy}
        onClick={stop(() => { setErr(null); publishNow.mutate(); })}
        title="Publish now to all active accounts for this project"
      >
        {publishNow.isPending ? 'Publishing…' : 'Publish now'}
      </button>
    );
  } else if (post.state === 'failed' && !blocked) {
    controls = (
      <button
        type="button"
        className="post-act post-act-retry"
        disabled={busy}
        onClick={stop(() => { setErr(null); publishNow.mutate(); })}
        title="Retry publishing — re-attempts the platforms that failed (won't double-post)"
      >
        {publishNow.isPending ? 'Retrying…' : 'Retry'}
      </button>
    );
  } else if (post.state === 'failed' && blocked) {
    // Content gate blocked it — there are no assets to publish; the fix is to
    // regenerate, which happens on the post page. The row itself opens the post.
    controls = <span className="post-act-blocked" title="Blocked by the content check — open the post to regenerate">Open to fix</span>;
  } else if (post.state === 'cancelled') {
    controls = (
      <button
        type="button"
        className="post-act post-act-retry"
        disabled={busy}
        onClick={stop(() => { setErr(null); regenerateFresh.mutate(); })}
        title="Retry — regenerate a fresh draft with a new angle"
      >
        {regenerateFresh.isPending ? 'Regenerating…' : 'Retry'}
      </button>
    );
  } else if (post.state === 'published' && post.backfill_count > 0) {
    // Accounts connected after this post published (e.g. IG/X added later) have
    // no publication row yet. Backfill them — publish-now is idempotent and skips
    // the platforms already published, so it never double-posts.
    const n = post.backfill_count;
    controls = (
      <button
        type="button"
        className="post-act post-act-publish"
        disabled={busy}
        onClick={stop(() => { setErr(null); publishNow.mutate(); })}
        title={`Publish to ${n} connected account${n === 1 ? '' : 's'} this post hasn’t reached yet — won’t double-post the ones already published`}
      >
        {publishNow.isPending ? 'Publishing…' : `Publish ${n} more`}
      </button>
    );
  }

  if (!controls) return <span className="post-actions-spacer" />;

  return (
    <span className="post-actions" onClick={(e) => e.preventDefault()}>
      {controls}
      {err && <span className="post-act-err" title={err} role="alert">⚠</span>}
    </span>
  );
}
